/**
 * Legacy URL → canonical URL redirect map for the Precision Pavers & Pool
 * Design SSR migration.
 *
 * Used by the catch-all route `src/routes/$.tsx` to emit TRUE HTTP 301 permanent
 * redirects (server-side, during SSR) for legacy URLs from the previous site,
 * while letting genuinely unknown URLs fall through to a real 404.
 *
 * This is the single source of truth for legacy redirects. It is designed to
 * handle every legacy URL previously covered by the Cloudflare Bulk Redirect
 * rule (112 entries) so that rule can eventually be retired without introducing
 * new 404s or redirect chains.
 *
 * Matching principles (per SEO migration spec):
 *  - City-specific legacy URL  → /areas/{city} (or /areas/broward-county fallback)
 *  - Service legacy URL (no city) → /services/{service}
 *  - Portfolio/gallery URL → /projects
 *  - Material/pattern URL → closest current material page
 *  - Never redirect to the homepage as a default.
 *
 * IMPORTANT — Orlando & Tampa are PRODUCT SHOWROOM / MATERIAL SALES / SUPPLY /
 * DELIVERY markets ONLY. Full installation service is offered only across
 * Broward, Miami-Dade, and Palm Beach counties. Therefore Orlando and Tampa
 * are deliberately EXCLUDED from `CITY_TO_AREA`: legacy installation-service
 * URLs for those cities (e.g. /pool-remodeling-orlando) are NOT redirected to
 * an area page — they fall through to a true 404 and are reported separately.
 * Only legacy material/product Orlando & Tampa URLs map to the showroom pages.
 */

/**
 * Legacy city slug → canonical area destination for FULL-SERVICE installation
 * markets (Broward, Miami-Dade, Palm Beach counties only).
 *
 * This single map drives EVERY city-suffixed legacy URL — bare city slugs
 * (/fort-lauderdale) and every service-prefix + city combination
 * (/pool-remodeling-fort-lauderdale, /driveway-pavers-weston, etc.) resolve to
 * the SAME destination for a given city, because the audit confirmed the
 * /areas/{city} (or /areas/broward-county parent) page is relevant for pool
 * remodeling, resurfacing, driveway pavers, artificial turf, and pool decks.
 *
 * Cities that have a current /areas/{slug} page map there; Broward cities
 * WITHOUT a dedicated page (Hollywood, Plantation, Pompano Beach, Deerfield
 * Beach, Wilton Manors) map to the Broward County parent page.
 *
 * Orlando and Tampa are intentionally ABSENT — they are product-showroom
 * markets where installation is not offered, so installation-service legacy
 * URLs for them must NOT redirect to an area page.
 */
const CITY_TO_AREA: Record<string, string> = {
  // Broward — cities with dedicated /areas pages
  "fort-lauderdale": "/areas/fort-lauderdale",
  "coral-springs": "/areas/coral-springs",
  "pembroke-pines": "/areas/pembroke-pines",
  weston: "/areas/weston",
  miramar: "/areas/miramar",
  sunrise: "/areas/sunrise",
  davie: "/areas/davie",
  // Miami-Dade — cities with dedicated /areas pages
  miami: "/areas/miami",
  "coral-gables": "/areas/coral-gables",
  brickell: "/areas/brickell",
  "coconut-grove": "/areas/coconut-grove",
  // Palm Beach — cities with dedicated /areas pages
  "boca-raton": "/areas/boca-raton",
  "delray-beach": "/areas/delray-beach",
  "boynton-beach": "/areas/boynton-beach",
  wellington: "/areas/wellington",
  // Broward cities WITHOUT a dedicated /areas page → geographic parent
  hollywood: "/areas/broward-county",
  "deerfield-beach": "/areas/broward-county",
  plantation: "/areas/broward-county",
  "pompano-beach": "/areas/broward-county",
  "wilton-manors": "/areas/broward-county",
};

/**
 * All recognized legacy city slugs (for bare-city matching and to drive the
 * service-prefix + city resolver). Sourced from the 112-entry Cloudflare set.
 */
const LEGACY_CITIES = Object.keys(CITY_TO_AREA);

/**
 * Orlando & Tampa product-showroom destinations. These cities host material
 * showrooms only, so legacy product/material URLs map to the showroom page.
 */
const SHOWROOM_CITIES: Record<string, string> = {
  orlando: "/areas/orlando",
  tampa: "/areas/tampa",
};

/**
 * Material / product keywords that, when suffixed with Orlando or Tampa,
 * identify a legacy showroom/material URL (NOT an installation service) and
 * map to that city's showroom page.
 */
const PRODUCT_MATERIAL_KEYWORDS = [
  "travertine",
  "travertine-pavers",
  "marble-pavers",
  "brick-pavers",
  "glass-tile",
  "glass-tile-mosaics",
  "pool-tile",
  "pool-coping",
  "diamond-brite",
];

/**
 * Service prefixes that, when suffixed with a full-service city, identify a
 * legacy city URL and resolve via `CITY_TO_AREA`. Every legacy service prefix
 * observed in the Cloudflare set is listed so the resolver is constrained to
 * known URL families and cannot intercept unrelated URLs.
 */
const SERVICE_PREFIXES = [
  "pool-remodeling",
  "pool-deck-remodeling",
  "driveway-pavers",
  "diamond-brite-pool-resurfacing",
  "paving-and-resurfacing",
  "artificial-grass-installation",
];

/**
 * Exact legacy path → canonical destination for URLs that do not follow the
 * "{service}-{city}" pattern: standalone service URLs, material/gallery URLs,
 * service sub-paths, and one-off legacy slugs.
 *
 * Route aliases handled by dedicated route files (/about-us, /portfolio,
 * /pool-deck-remodeling, /swimming-pool-remodeling, /paving-and-resurfacing)
 * are intentionally NOT listed here — they are matched by the router before
 * the catch-all and never reach `resolveLegacyRedirect`.
 */
const LEGACY_EXACT_REDIRECTS: Record<string, string> = {
  // --- Standalone material / gallery URLs → closest current material page ---
  "/patterns": "/brick-pavers",
  "/colors": "/brick-pavers",
  "/paver-options": "/brick-pavers",
  "/glass-tile-options": "/glass-tile-mosaics",

  // --- Standalone service URLs (no city) → canonical service page ---
  "/artificial-grass": "/services/outdoor-enhancements",
  "/artificial-turf": "/services/outdoor-enhancements",
  "/pool-lighting-installation": "/services/outdoor-enhancements",
  "/outdoor-lighting": "/services/outdoor-enhancements",
  "/pool-water-features-installation": "/services/outdoor-enhancements",
  "/pressure-washing-sealing": "/services/outdoor-enhancements",
  "/diamond-brite-pool-resurfacing": "/services/pool-remodeling",
  "/fencing-services": "/services/fencing",

  // --- Showroom → homepage (showroom info lives on the homepage) ---
  "/showroom": "/",

  // --- One-off Broward legacy slug → Broward County parent ---
  "/beach-driveway-pavers-pompano": "/areas/broward-county",

  // --- One-off legacy slug with "and" → canonical terms page ---
  "/terms-and-conditions": "/terms-conditions",

  // --- Service sub-paths → parent service page ---
  "/paving-and-resurfacing/patio-installation":
    "/services/driveway-patio-pavers",
  "/paving-and-resurfacing/driveway-paving": "/services/driveway-patio-pavers",
  "/paving-and-resurfacing/walkway-resurfacing":
    "/services/driveway-patio-pavers",
  "/pool-deck-remodeling/deck-expansion": "/services/pool-deck-remodeling",
  "/pool-deck-remodeling/custom-deck-design": "/services/pool-deck-remodeling",
  "/pool-deck-remodeling/deck-repair": "/services/pool-deck-remodeling",
  "/pool-deck-remodeling/deck-resurfacing": "/services/pool-deck-remodeling",
  "/swimming-pool-remodeling/pool-resurfacing": "/services/pool-remodeling",
  "/swimming-pool-remodeling/tile-replacement": "/services/pool-remodeling",
  "/swimming-pool-remodeling/pool-design-enhancements":
    "/services/pool-remodeling",
  "/swimming-pool-remodeling/pool-equipment-upgrades":
    "/services/pool-remodeling",
  "/retention/retaining-wall-construction": "/services/outdoor-enhancements",
};

/**
 * Resolve a legacy request path (no query string) to a canonical destination,
 * or return null when the path has no legitimate historical equivalent (so the
 * caller can emit a true 404 instead of a soft-404 redirect).
 *
 * @param path pathname beginning with "/", e.g. "/pool-remodeling-weston"
 */
export function resolveLegacyRedirect(path: string): string | null {
  // Normalize: leading slash required, strip trailing slashes.
  let p = path.startsWith("/") ? path : `/${path}`;
  p = p.replace(/\/+$/, "");
  if (!p || p === "/") return null;

  // 1. Exact legacy mappings take precedence (handles standalone service,
  //    material, sub-path, and one-off URLs).
  if (LEGACY_EXACT_REDIRECTS[p]) return LEGACY_EXACT_REDIRECTS[p];

  // 2. Orlando / Tampa showroom markets: only material/product keywords map to
  //    the showroom page. Installation-service keywords are deliberately NOT
  //    redirected (return null → true 404) because installation is not offered
  //    in these markets.
  for (const [city, dest] of Object.entries(SHOWROOM_CITIES)) {
    const suffix = `-${city}`;
    if (p.endsWith(suffix)) {
      const prefix = p.slice(0, p.length - suffix.length).replace(/^\/+/, "");
      if (prefix && PRODUCT_MATERIAL_KEYWORDS.includes(prefix)) return dest;
      // Installation-service (or unrecognized) Orlando/Tampa URL → no redirect.
      return null;
    }
  }

  // 3. Bare city slug → /areas/{city} (or /areas/broward-county parent).
  const slug = p.slice(1);
  if (LEGACY_CITIES.includes(slug)) return CITY_TO_AREA[slug];

  // 4. Service-prefix + city → same destination as the bare city. Every
  //    recognized service prefix uses CITY_TO_AREA so the destination is
  //    identical whether the legacy URL was /pool-remodeling-weston,
  //    /driveway-pavers-weston, or just /weston.
  for (const prefix of SERVICE_PREFIXES) {
    const prefixSlash = `/${prefix}-`;
    if (p.startsWith(prefixSlash)) {
      const city = p.slice(prefixSlash.length);
      if (CITY_TO_AREA[city]) return CITY_TO_AREA[city];
      // Unknown city under a known service prefix → no confident match → 404.
      return null;
    }
  }

  // No confident legacy match → caller should 404.
  return null;
}
